/**
 * PayrollUploader — Dropzone para el .txt del homebanking (Banco Provincia).
 *
 * Sube el archivo a /api/payroll/parse, muestra vista previa (76 beneficiarios,
 * total, warnings), valida el monto contra el total de la OP y —si difiere—
 * pide un motivo obligatorio del desvío.
 */
import { useRef, useState, useEffect } from 'react';

import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Upload, FileText, AlertTriangle, CheckCircle2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(n || 0));

export function PayrollUploader({ opTotal, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [varianceReason, setVarianceReason] = useState('');

  // Diferencia entre monto del archivo y monto de la OP
  const variance = parsed ? Number(parsed.total_amount || 0) - Number(opTotal || 0) : 0;
  const hasVariance = parsed ? Math.abs(variance) > 1.0 : false;

  // Mantener onChange en ref para evitar loops por referencia cambiante en el padre
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Solo notificar al padre cuando cambian los datos reales del uploader
  useEffect(() => {
    if (!parsed) { onChangeRef.current?.(null); return; }
    onChangeRef.current?.({
      payroll_beneficiaries: parsed.beneficiaries,
      payroll_attachment: parsed.attachment,
      payroll_variance_reason: hasVariance ? (varianceReason.trim() || null) : null,
      total_amount: parsed.total_amount,
      has_variance: hasVariance,
      variance_ok: !hasVariance || !!varianceReason.trim(),
    });
  }, [parsed, varianceReason, hasVariance]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(txt|dat)$/i)) {
      toast.error('El archivo debe ser .txt o .dat');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/payroll/parse`, fd, { withCredentials: true });
      setParsed(res.data);
      toast.success(`Se leyeron ${res.data.count} beneficiarios (${fmt(res.data.total_amount)})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al parsear el archivo');
    } finally { setUploading(false); }
  };

  const clear = () => { setParsed(null); setVarianceReason(''); if (fileRef.current) fileRef.current.value = ''; };

  if (!parsed) {
    return (
      <div className="border-2 border-dashed border-violet-300 bg-violet-50/40 rounded-lg p-6 text-center space-y-3">
        <Upload className="w-10 h-10 mx-auto text-violet-500" />
        <div>
          <p className="text-sm font-semibold text-violet-900">Adjuntá el archivo .txt del homebanking</p>
          <p className="text-xs text-muted-foreground mt-1">Banco Provincia — formato posicional (extensión .txt o .dat)</p>
        </div>
        <input ref={fileRef} type="file" accept=".txt,.dat,text/plain" className="hidden"
               onChange={(e) => upload(e.target.files?.[0])}
               data-testid="payroll-file-input" />
        <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="bg-violet-600 hover:bg-violet-700" data-testid="payroll-upload-btn">
          {uploading ? 'Leyendo…' : 'Elegir archivo'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border p-3 flex items-center justify-between bg-emerald-50/40 border-emerald-200">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-emerald-700" />
          <div>
            <div className="text-sm font-medium">{parsed.attachment?.filename}</div>
            <div className="text-xs text-muted-foreground">
              Emisor CUIT {parsed.emitter_cuit} · {parsed.concept} · Acred. {parsed.credit_date} · Nº {parsed.file_number}
            </div>
          </div>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={clear} title="Quitar archivo" data-testid="payroll-clear-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-md border bg-white p-2 text-center">
          <div className="text-xs text-muted-foreground uppercase">Empleados</div>
          <div className="text-xl font-bold text-slate-800" data-testid="payroll-count">{parsed.count}</div>
        </div>
        <div className="rounded-md border bg-white p-2 text-center">
          <div className="text-xs text-muted-foreground uppercase">Total del archivo</div>
          <div className="text-xl font-bold text-emerald-700" data-testid="payroll-total">{fmt(parsed.total_amount)}</div>
        </div>
        <div className={`rounded-md border p-2 text-center ${hasVariance ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}>
          <div className="text-xs uppercase font-semibold">
            {hasVariance ? 'Diferencia con OP' : 'Coincide'}
          </div>
          <div className={`text-lg font-bold ${hasVariance ? 'text-amber-800' : 'text-emerald-800'}`} data-testid="payroll-variance">
            {hasVariance ? fmt(variance) : <CheckCircle2 className="inline w-6 h-6" />}
          </div>
        </div>
      </div>

      {hasVariance && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <Label className="text-amber-900 font-semibold text-sm">Motivo del desvío (obligatorio)</Label>
              <Textarea
                value={varianceReason}
                onChange={(e) => setVarianceReason(e.target.value)}
                placeholder={`El archivo suma ${fmt(parsed.total_amount)} y la OP ${fmt(opTotal)}. Aclará por qué.`}
                className="mt-1 min-h-[70px] bg-white"
                data-testid="payroll-variance-reason"
              />
            </div>
          </div>
        </div>
      )}

      <div className="border rounded-md max-h-64 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="p-2 text-left">#</th>
              <th className="p-2 text-left">CUIL</th>
              <th className="p-2 text-left">Beneficiario</th>
              <th className="p-2 text-left">CBU</th>
              <th className="p-2 text-right">Importe</th>
            </tr>
          </thead>
          <tbody>
            {parsed.beneficiaries.map((b, i) => (
              <tr key={i} className="border-t hover:bg-slate-50">
                <td className="p-2 text-slate-500">{i + 1}</td>
                <td className="p-2 font-mono">{b.cuil}</td>
                <td className="p-2">{b.beneficiary_name}</td>
                <td className="p-2 font-mono text-slate-500">{b.cbu}</td>
                <td className="p-2 text-right font-mono">{fmt(b.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {parsed.warnings?.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          <strong>Advertencias:</strong>
          <ul className="list-disc pl-5 mt-1">
            {parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
